import type {
  AhaMomentStatus,
  FileKindCounts,
  FileKindId,
  InstrumentTechniqueId,
  ChecklistExplicitCompletionKey,
  OnboardingChecklistResponse,
  PaginatedResponse,
  WorkspaceResearchContext,
} from '@bower/shared';
import { BULK_DELETE_MAX_IDS } from '@bower/shared/constants/bulk';
import { unwrapApiResponse } from './http-client.types';
import type { HttpClient } from './http-client.types';

/**
 * Update the WORKSPACE research context (field / lab size). Owner/admin only
 * (enforced server-side). Personal role is edited via the user update path,
 * not here. Workspace context comes from the default x-workspace-id header.
 */
export async function updateWorkspaceResearchContext(
  http: HttpClient,
  patch: { field?: string | null; fieldOther?: string | null; labSize?: string | null },
): Promise<WorkspaceResearchContext> {
  const res = await http.patch<{ workspaceContext: WorkspaceResearchContext }>(
    '/workspace/settings/research-context',
    patch,
  );
  if (!res.success || !res.data) throw new Error('Failed to update research context');
  return res.data.workspaceContext;
}

export interface GetAuditLogsParams {
  entityType?: string;
  entityId?: string;
  userId?: string;
  action?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
  limit?: number;
  cursor?: string;
}

export interface ExportAuditLogsParams {
  entityType?: string;
  userId?: string;
  action?: string;
  /** Comma-separated `AUDIT_BOWER_CLIENTS` values; the CSV accepts what the list accepts. */
  bowerClient?: string;
  startDate?: string;
  endDate?: string;
  search?: string;
}

export function getWorkspaceUsers(http: HttpClient) {
  return http.get('/workspace/users');
}

export function getAuditLogs(http: HttpClient, params?: GetAuditLogsParams) {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
  }
  const qs = queryParams.toString();
  return http.get(`/audit${qs ? `?${qs}` : ''}`);
}

/** Result of the last hash-chain verification of this workspace's audit trail. */
export type AuditIntegrity =
  | { state: 'verified' | 'failed'; verifiedAt: string }
  | { state: 'not_verified' };

export async function getAuditIntegrity(http: HttpClient): Promise<AuditIntegrity> {
  return unwrapApiResponse(
    await http.get<AuditIntegrity>('/audit/integrity'),
    'Failed to fetch audit integrity',
  );
}

export async function exportAuditLogs(
  http: HttpClient,
  params?: ExportAuditLogsParams,
): Promise<Blob> {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
  }
  const qs = queryParams.toString();
  const url = `/audit/export${qs ? `?${qs}` : ''}`;
  const response = await http.getClient().get(url, { responseType: 'blob' });
  return response.data as Blob;
}

export async function getOnboardingChecklist(
  http: HttpClient,
  workspaceId: string,
): Promise<OnboardingChecklistResponse> {
  const res = await http.get<OnboardingChecklistResponse>('/onboarding-checklist', {
    headers: { 'x-workspace-id': workspaceId },
  });
  if (!res.success || !res.data) throw new Error('Failed to fetch onboarding checklist');
  return res.data;
}

export async function dismissOnboardingChecklist(http: HttpClient): Promise<void> {
  // Was cast straight to Promise<void>, so a resolved { success: false }
  // reported success and the optimistic hide never rolled back (rule 12).
  unwrapApiResponse(
    await http.patch('/onboarding-checklist/dismiss'),
    'Failed to dismiss checklist',
  );
}

/**
 * "Hide for now" (collapsed), "Don't show again" (dismissed) or "Show setup
 * guide" (shown) for this workspace's unified setup list. Workspace context
 * comes from the client's default `x-workspace-id` interceptor.
 */
export async function setOnboardingChecklistVisibility(
  http: HttpClient,
  visibility: 'collapsed' | 'dismissed' | 'shown',
): Promise<void> {
  unwrapApiResponse(
    await http.patch('/onboarding-checklist/visibility', { visibility }),
    'Failed to update the setup list',
  );
}

export async function recordChecklistExplicitCompletion(
  http: HttpClient,
  stepKey: ChecklistExplicitCompletionKey,
): Promise<void> {
  unwrapApiResponse(
    await http.patch('/onboarding-checklist/explicit-completion', { stepKey }),
    'Failed to record checklist completion',
  );
}

/**
 * Attempt to record the activation aha moment (first chat response with
 * workspace content). Returns the server's status so callers know whether
 * to retry later (`no_content`) or stop. Workspace context comes from the
 * client's default `x-workspace-id` interceptor.
 */
export async function recordAhaMoment(http: HttpClient): Promise<AhaMomentStatus> {
  const res = await http.post<{ status: AhaMomentStatus }>('/onboarding-checklist/aha');
  if (!res.success || !res.data) throw new Error('Failed to record aha moment');
  return res.data.status;
}

export function getTrash(http: HttpClient, params: Record<string, string | number> = {}) {
  const base = '/trash';
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  const url = qs ? `${base}?${qs}` : base;
  return http.get<
    PaginatedResponse<{
      id: string;
      name: string;
      type: 'artifact' | 'project';
      artifactType?: string;
      createdAt: string;
      createdByName: string | null;
      deletedAt: string;
      deletedByName: string | null;
      parentEntityType?: string;
      parentEntityId?: string;
      fileKind?: FileKindId;
      technique?: InstrumentTechniqueId;
      fileExtension?: string | null;
      mimeType?: string | null;
    }>
  >(url);
}

/** The Type menu's per-kind counts for the Trash list under its search and Deleted by filters. */
export function getTrashKindCounts(
  http: HttpClient,
  filters: { readonly search?: string; readonly deletedBy?: readonly string[] },
) {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.deletedBy?.length) params.set('deletedBy', filters.deletedBy.join(','));
  const qs = params.toString();
  return http.get<{ counts: FileKindCounts }>(
    qs ? `/trash/kind-counts?${qs}` : '/trash/kind-counts',
  );
}

// The trash mutations unwrap (throw on { success: false }) so callers'
// onError/catch paths are reachable. The client never rejects on HTTP
// errors, so returning the raw envelope here made every failure fire the
// success path — "Restored 0 items" success toasts, ITEM_RESTORED analytics
// and undo confirmations for operations that did not happen (PR #472 review).
export async function restoreTrashItem(
  http: HttpClient,
  id: string,
  type: 'artifact' | 'project',
): Promise<{ restoredToRoot: boolean }> {
  // retries: 0 — a non-idempotent POST, same rule as the bulk routes below.
  return unwrapApiResponse(
    await http.request<{ restoredToRoot: boolean }>(
      { method: 'POST', url: `/trash/${id}/restore`, data: { type } },
      0,
    ),
    'Failed to restore item',
  );
}

export async function permanentlyDeleteTrashItem(
  http: HttpClient,
  id: string,
  type: 'artifact' | 'project',
): Promise<void> {
  // retries: 0: a non-idempotent, irreversible DELETE, same rule as the
  // bulk routes below.
  unwrapApiResponse(
    await http.request({ method: 'DELETE', url: `/trash/${id}`, data: { type } }, 0),
    'Failed to permanently delete item',
  );
}

// The trash bulk routes cap batches (trash.controller.ts bulkItemsSchema —
// abuse guard). A legitimate batch can exceed the cap: Trash select-all, and
// the undo path restores every id a >cap chunked bulk delete just trashed.
// So the client chunks; chunk size and the server .max() derive from the same
// shared constant, so they cannot drift (PR #507/#508 reviews).
const TRASH_BULK_CHUNK_SIZE = BULK_DELETE_MAX_IDS;

// retries: 0 — both are non-idempotent mutation POSTs; the client's default
// retry replays them on 5xx/timeout. A replayed PERMANENT delete
// auto-continues destruction after a mid-batch failure, and a replayed
// restore reports failures for items the first attempt already restored
// (PR #500/#501 adversarial reviews).
async function postTrashBulkInChunks(
  http: HttpClient,
  url: '/trash/bulk-restore' | '/trash/bulk-delete',
  items: Array<{ id: string; type: 'artifact' | 'project' }>,
  fallbackMessage: string,
): Promise<{ succeeded: number; failed: number }> {
  // Single batch keeps the pre-chunking contract: throw on a failure
  // envelope (callers catch and toast).
  if (items.length <= TRASH_BULK_CHUNK_SIZE) {
    return unwrapApiResponse(
      await http.request<{ succeeded: number; failed: number }>(
        { method: 'POST', url, data: { items } },
        0,
      ),
      fallbackMessage,
    );
  }

  // Multi-chunk mirrors bulkOperations.ts's rule-12 convention: a later
  // chunk's failure must never discard an earlier chunk's completed work —
  // for /trash/bulk-delete that work is IRREVERSIBLE, and throwing here made
  // the caller report "failed" (and skip query invalidation) while hundreds
  // of items were already gone (PR #508 review). On a failed chunk envelope:
  // stop issuing further mutations, count that chunk and every unattempted
  // item as failed (fail closed), and RETURN the true totals so onSuccess
  // paths render accurate counts and invalidate.
  const totals = { succeeded: 0, failed: 0 };
  for (let i = 0; i < items.length; i += TRASH_BULK_CHUNK_SIZE) {
    const chunk = items.slice(i, i + TRASH_BULK_CHUNK_SIZE);
    const response = await http.request<{ succeeded: number; failed: number }>(
      { method: 'POST', url, data: { items: chunk } },
      0,
    );
    const data = response.data;
    if (
      response.success &&
      typeof data?.succeeded === 'number' &&
      typeof data?.failed === 'number'
    ) {
      totals.succeeded += data.succeeded;
      totals.failed += data.failed;
    } else {
      totals.failed += items.length - i;
      break;
    }
  }
  return totals;
}

export async function bulkRestoreTrashItems(
  http: HttpClient,
  items: Array<{ id: string; type: 'artifact' | 'project' }>,
): Promise<{ succeeded: number; failed: number }> {
  return postTrashBulkInChunks(http, '/trash/bulk-restore', items, 'Failed to restore items');
}

export async function bulkPermanentlyDeleteTrashItems(
  http: HttpClient,
  items: Array<{ id: string; type: 'artifact' | 'project' }>,
): Promise<{ succeeded: number; failed: number }> {
  return postTrashBulkInChunks(
    http,
    '/trash/bulk-delete',
    items,
    'Failed to permanently delete items',
  );
}
